import { $isCodeNode } from "@lexical/code-core";
import { $findMatchingParent } from "@lexical/utils";
import { $createQuoteNode, $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import {
  $createParagraphNode,
  $getSelection,
  $isLineBreakNode,
  $isParagraphNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  defineExtension,
  DELETE_CHARACTER_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_ENTER_COMMAND,
  type LexicalExtension,
} from "lexical";

const isMod = (event: KeyboardEvent): boolean => event.metaKey || event.ctrlKey;

/** Mod+Enter leaves a code block by putting the caret in a paragraph directly after it. */
export const CodeBlockExitExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/code-block-exit",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/code-block-exit",
  register: (editor) =>
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (event === null || !isMod(event)) return false;
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;
        const code = $findMatchingParent(selection.anchor.getNode(), $isCodeNode);
        if (code === null) return false;
        event.preventDefault();
        const next = code.getNextSibling();
        if (next !== null && next.getType() === "paragraph" && next.getTextContentSize() === 0) {
          next.selectStart();
        } else {
          code.insertAfter($createParagraphNode()).selectStart();
        }
        return true;
      },
      COMMAND_PRIORITY_NORMAL,
    ),
});

/**
 * A line of three backticks that ends a code block, then Enter, closes the block the way it was
 * opened: the fence goes, and the caret lands in a paragraph after the block. Only on the last
 * line, with a line of code above it and the caret at its end; backticks anywhere else are code.
 */
export const CodeFenceCloseExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/code-fence-close",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/code-fence-close",
  register: (editor) =>
    editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (event === null || isMod(event) || event.shiftKey || editor.isComposing()) return false;
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
        const code = $findMatchingParent(selection.anchor.getNode(), $isCodeNode);
        if (code === null) return false;
        const children = code.getChildren();
        const lastBreak = children.findLastIndex($isLineBreakNode);
        if (lastBreak === -1) return false;
        const fence = children.slice(lastBreak + 1);
        if (fence.map((node) => node.getTextContent()).join("") !== "```") return false;
        // The caret is at the end of the block, which is the end of the fence.
        const { anchor } = selection;
        const last = fence.at(-1);
        const atEnd =
          anchor.type === "text"
            ? last !== undefined &&
              anchor.getNode().is(last) &&
              anchor.offset === last.getTextContentSize()
            : anchor.getNode().is(code) && anchor.offset === children.length;
        if (!atEnd) return false;
        event.preventDefault();
        for (const node of [children[lastBreak]!, ...fence]) node.remove();
        const next = code.getNextSibling();
        if (next !== null && next.getType() === "paragraph" && next.getTextContentSize() === 0) {
          next.selectStart();
        } else {
          code.insertAfter($createParagraphNode()).selectStart();
        }
        return true;
      },
      COMMAND_PRIORITY_NORMAL,
    ),
});

/**
 * Backspace in an empty heading, quote, or code block turns it back into a paragraph. Stock
 * behavior removes the block and moves the caret into the one above, which reads as the editor
 * deleting something the user did not aim at. List items already fall back on their own.
 */
export const EmptyBlockBackspaceExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/empty-block-backspace",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/empty-block-backspace",
  register: (editor) =>
    editor.registerCommand(
      DELETE_CHARACTER_COMMAND,
      (isBackward) => {
        if (!isBackward) return false;
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return false;
        const block = selection.anchor.getNode().getTopLevelElement();
        if (block === null || !$isRootOrShadowRoot(block.getParent())) return false;
        if (!($isHeadingNode(block) || $isQuoteNode(block) || $isCodeNode(block))) return false;
        if (block.getTextContentSize() > 0 || block.getChildrenSize() > 0) return false;
        block.replace($createParagraphNode()).select();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    ),
});

/**
 * Enter inside a quote stays in the quote, and Enter in an empty quote leaves it, the way a list
 * item works. Upstream's `QuoteNode.insertNewAfter` always continues with a paragraph, wherever the
 * caret is: the text after it left the quote, and at the very start the whole text did, leaving an
 * empty quote above. Decided by the owner on 2026-09-18.
 *
 * - In an empty quote: the quote becomes a paragraph.
 * - At the very start: an empty paragraph goes in above, as a heading does it.
 * - Anywhere else: the quote splits in two, or a new empty quote follows it.
 */
export const QuoteEnterExtension: LexicalExtension<
  Record<never, never>,
  "z-editor/quote-enter",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/quote-enter",
  register: (editor) =>
    editor.registerCommand(
      INSERT_PARAGRAPH_COMMAND,
      () => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return false;
        const quote = selection.anchor.getNode().getTopLevelElement();
        if (!$isQuoteNode(quote) || quote.isShadowRoot()) return false;
        if (!$isQuoteNode(selection.focus.getNode().getTopLevelElement())) return false;
        if (!selection.isCollapsed()) selection.removeText();
        if (quote.isEmpty()) {
          quote.replace($createParagraphNode()).select();
          return true;
        }
        const { anchor } = selection;
        const first = quote.getFirstDescendant();
        const atStart =
          anchor.offset === 0 && (anchor.key === quote.getKey() || anchor.key === first?.getKey());
        if (atStart) {
          quote.insertBefore($createParagraphNode());
          return true;
        }
        const after = selection.insertParagraph();
        if (!$isParagraphNode(after)) return true;
        const next = $createQuoteNode()
          .setFormat(quote.getFormatType())
          .setDirection(quote.getDirection());
        after.replace(next, true);
        next.selectStart();
        return true;
      },
      COMMAND_PRIORITY_LOW,
    ),
});
